import { makeAutoObservable } from 'mobx'

/** Сповіщення так, як його віддає `/services/user/notifications`. */
export interface Notification {
    id?: number
    uuid: string

    type: string
    /**
     * Код події каталогу (ADR 040): `activity_overdue`… За ним дзвіночок
     * малює підпис, значок і тон; `null` — сповіщення без коду.
     */
    code?: string | null
    title: string
    message: string

    entityCode?: string | null
    entityUuid?: string | null

    /** Хто спричинив подію; `null` — система (прострочення ставить планувальник). */
    authorName?: string | null

    isReaded: boolean
    readedAt?: string | null

    createdAt?: string | null
}

/** Що показує шухляда: усі сповіщення чи лише непрочитані. */
export type NotificationFilter = 'all' | 'unread'

interface Pagination {
    page?: number
    total?: number
}

class NotificationStoreClass {
    // ---------- UI ----------
    isDrawerOpen = false
    filter: NotificationFilter = 'all'
    /** Перша сторінка — замість списку скелет. */
    isLoadingFirstPage = false
    /** Наступна сторінка — список лишається, внизу крутиться. */
    isLoadingMore = false
    isMarkingAll = false

    // ---------- DATA ----------
    notifications: Notification[] = []
    page = 1
    total = 0

    /**
     * Скільки непрочитаних — для дзвіночка й вкладки «Непрочитані».
     *
     * Окремо від списку: у списку лише завантажені сторінки, а лічильник має
     * бути правдою про все.
     */
    unreadCount = 0

    constructor() {
        makeAutoObservable(this)
    }

    get hasMore(): boolean {
        return this.notifications.length < this.total
    }

    // ---------- UI ----------
    setIsDrawerOpen(value: boolean): void {
        this.isDrawerOpen = value
    }

    setFilter(value: NotificationFilter): void {
        this.filter = value
    }

    setLoading(page: number, value: boolean): void {
        if (page > 1) {
            this.isLoadingMore = value
        } else {
            this.isLoadingFirstPage = value
        }
    }

    setIsMarkingAll(value: boolean): void {
        this.isMarkingAll = value
    }

    // ---------- DATA ----------
    setPage(items: Notification[], { page, total }: Pagination): void {
        this.notifications = page !== undefined && page > 1 ? [...this.notifications, ...items] : items
        if (page !== undefined) this.page = page
        if (total !== undefined) this.total = total
    }

    reset(): void {
        this.notifications = []
        this.page = 1
        this.total = 0
    }

    setUnreadCount(value: number): void {
        this.unreadCount = Math.max(0, value)
    }

    /** Позначити прочитаними в списку й зменшити лічильник на стільки, скільки справді змінилося. */
    markRead(uuids: string[]): void {
        const now = new Date().toISOString()
        let changed = 0

        this.notifications = this.notifications.map((item) => {
            if (!uuids.includes(item.uuid) || item.isReaded) return item

            changed += 1

            return { ...item, isReaded: true, readedAt: now }
        })

        this.unreadCount = Math.max(0, this.unreadCount - changed)
    }

    markAllRead(): void {
        const now = new Date().toISOString()

        this.notifications = this.notifications.map((item) =>
            item.isReaded ? item : { ...item, isReaded: true, readedAt: now }
        )
        this.unreadCount = 0
    }

    /** Нове сповіщення, що прийшло в реальному часі. */
    receive(item: Notification): void {
        if (!item.isReaded) this.unreadCount += 1

        // Список, якого зараз ніхто не бачить, перечитається при відкритті
        if (!this.isDrawerOpen || this.notifications.some((existing) => existing.uuid === item.uuid)) return

        this.notifications = [item, ...this.notifications]
        this.total += 1
    }

    /** Сповіщення видалили на сервері (разом із записом, про який воно). */
    discard(uuid: string, wasRead: boolean): void {
        const existing = this.notifications.find((item) => item.uuid === uuid)

        if (existing) {
            this.notifications = this.notifications.filter((item) => item.uuid !== uuid)
            this.total = Math.max(0, this.total - 1)
        }

        if (!(existing?.isReaded ?? wasRead)) {
            this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
    }
}

export const NotificationStore = new NotificationStoreClass()
